import { useEffect, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Rocket, Zap, Search, TrendingUp, Bot, ArrowRight, ShieldCheck, Sparkles, Check, Crown } from "lucide-react";
import { CTASection } from "../components/CTASection";
import { DealsSignup } from "../components/DealsSignup";
import { StarRating } from "../components/cards";
import { api } from "../lib/api";
import { useSeo, SITE_URL } from "../lib/seo";
import { AREAS } from "../lib/areas";

const Scene3D = lazy(() => import("../components/Scene3D").then((m) => ({ default: m.Scene3D })));

const stats = [
  { k: "$299", v: "Website Build" },
  { k: "from $149/mo", v: "Growth Platform" },
  { k: "<2s", v: "Load Times" },
  { k: "24/7", v: "AI Automation" },
];

const PLAN_ICONS = { gp_essential: ShieldCheck, gp_growth: Rocket, gp_pro: Zap, gp_scale: Crown };

const pillars = [
  { icon: Zap, t: "Blazing Page Speed", d: "Optimized Core Web Vitals so you rank higher and convert more." },
  { icon: Search, t: "Local SEO Built-In", d: "Engineered to dominate San Antonio search results." },
  { icon: TrendingUp, t: "Sales-Focused", d: "Every page designed to generate leads and close sales." },
  { icon: Bot, t: "Auto Follow-Up", d: "Text & email automations keep your customers coming back." },
];

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/reviews").then(({ data }) => setReviews(data.items.slice(0, 9))).catch(() => {}); }, []);
  useEffect(() => { api.get("/accelerator/plans").then(({ data }) => setPlans((data.plans || []).filter((p) => !p.custom))).catch(() => {}); }, []);
  useSeo({
    title: "San Antonio Web Design & SEO | 3D Websites, Digital Marketing — $299 Build",
    description: "San Antonio's Web Design: award-winning web design, local SEO, digital marketing, PPC ad management, IT consulting, PC & mobile repair and secure hosting. High-converting 3D websites for a $299 build + $100/mo. Serving San Antonio, Leon Valley, Live Oak, Converse, Schertz, Stone Oak, Boerne & all of Bexar County.",
    path: "/",
    type: "website",
    keywords: "San Antonio web design, San Antonio web designer, web design company San Antonio, San Antonio SEO, local SEO San Antonio, digital marketing San Antonio, PPC advertising San Antonio, ecommerce web design, WordPress web design, custom website design, affordable web design Texas, small business website design, IT consulting San Antonio, computer repair San Antonio, mobile app design, secure web hosting, Google Business Profile optimization, web design near me, Bexar County web design",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": SITE_URL + "/#org",
          "name": "San Antonio's Web Design",
          "url": SITE_URL,
          "logo": SITE_URL + "/logo.png",
          "description": "Award-winning web design, SEO, digital marketing, IT consulting and repair for San Antonio businesses.",
          "areaServed": AREAS.map((a) => ({ "@type": "City", "name": a.name })),
          "sameAs": [
            "https://www.facebook.com/", "https://twitter.com/", "https://www.instagram.com/",
            "https://www.linkedin.com/", "https://www.youtube.com/",
          ],
        },
        {
          "@type": "WebSite",
          "@id": SITE_URL + "/#website",
          "url": SITE_URL,
          "name": "San Antonio's Web Design",
          "publisher": { "@id": SITE_URL + "/#org" },
        },
        {
          "@type": "LocalBusiness",
          "name": "San Antonio's Web Design",
          "image": SITE_URL + "/logo.png",
          "url": SITE_URL,
          "areaServed": AREAS.map((a) => a.name),
          "priceRange": "$$",
        },
      ],
    },
  });

  return (
    <>
      <Suspense fallback={null}><Scene3D /></Suspense>

      <section className="relative min-h-[92vh] flex items-center" data-testid="hero">
        <div className="mx-auto max-w-7xl px-6 py-24 w-full">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-sm font-semibold text-primary mb-6">
              <ShieldCheck className="h-4 w-4" /> San Antonio, TX · Web · Marketing · IT
            </span>
            <h1 className="font-head text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95]">
              Websites that <span className="text-gradient">generate leads</span> & close sales.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              We build high-end, lightning-fast, sales-focused websites for just <b className="text-foreground">$299</b> — then our <b className="text-foreground">AI Business Growth Platform</b> handles hosting, SEO, content, social & lead follow-ups from <b className="text-foreground">$149/mo</b> so you can focus on running your business.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/get-started" data-testid="hero-order-now"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-13 py-3.5 font-semibold hover:brightness-110 transition-[filter] shadow-xl shadow-primary/30">
                <Rocket className="h-5 w-5" /> Get Started — from $299
              </Link>
            </div>
            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {stats.map((s) => (
                <div key={s.v} className="rounded-2xl glass-lite p-4">
                  <div className="font-head text-2xl font-extrabold text-primary">{s.k}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pt-8" data-testid="home-growth-banner">
        <Link to="/ai-business-growth" className="group block rounded-3xl bg-primary text-primary-foreground p-8 md:p-10 grid-bg overflow-hidden relative hover:brightness-105 transition-[filter]">
          <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground/15 px-3 py-1 text-xs font-bold uppercase tracking-wide">
                <Sparkles className="h-3.5 w-3.5" /> Free · AI Business Growth Report
              </span>
              <h2 className="font-head text-3xl md:text-4xl font-black mt-4 leading-tight">
                Find out how much growth your business is missing
              </h2>
              <p className="opacity-90 mt-2 text-base md:text-lg">
                Get a <b>free AI-powered growth report</b> analyzing your website, SEO, competitors & opportunities to win more customers.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-foreground text-primary px-7 h-13 py-3.5 font-bold shrink-0 shadow-lg group-hover:translate-x-1 transition-transform">
              <Search className="h-5 w-5" /> Generate my free report <ArrowRight className="h-5 w-5" />
            </span>
          </div>
        </Link>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-8" data-testid="pillars">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {pillars.map((p) => (
            <div key={p.t} className="rounded-2xl glass-lite p-6 hover:-translate-y-1 transition-transform">
              <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center mb-4"><p.icon className="h-5 w-5" /></div>
              <h3 className="font-head font-bold text-lg">{p.t}</h3>
              <p className="text-sm text-muted-foreground mt-2">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16" data-testid="home-services">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="font-head text-3xl md:text-4xl font-extrabold">Everything your business needs</h2>
            <p className="text-muted-foreground mt-2">One partner for web, marketing, IT, repair & security.</p>
          </div>
          <Link to="/services" className="hidden sm:inline-flex items-center gap-1 text-primary font-semibold">All services <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { t: "High-End Web Design", d: "$299 build + Growth Platform from $149/mo. Sales-focused, SEO-ready, fast.", to: "/services/web-design" },
            { t: "Marketing & Paid Ads", d: "Rookie $300 · Startup $500 · To The Moon $1000 per month.", to: "/services/marketing" },
            { t: "Computer & Mobile Repair", d: "Fast, affordable repair for homes and businesses.", to: "/services/computer-repair" },
            { t: "IT & Business Consulting", d: "Get a custom quote and scale with confidence.", to: "/services/business-consulting" },
            { t: "Security Camera Setup", d: "Professional smart camera installation & config.", to: "/services/security-cameras" },
            { t: "Secure Hosting", d: "99.9% uptime managed hosting for $100/month.", to: "/services/secure-hosting" },
          ].map((s) => (
            <Link key={s.t} to={s.to} className="group rounded-2xl glass-lite p-6 hover:-translate-y-1 transition-transform">
              <h3 className="font-head font-bold text-lg group-hover:text-primary transition-colors">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-2">{s.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16" data-testid="home-plans">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-sm font-semibold text-primary"><Sparkles className="h-4 w-4" /> AI Business Growth Platform</span>
          <h2 className="font-head text-3xl md:text-4xl font-extrabold mt-5">Your marketing department, in a box</h2>
          <p className="text-muted-foreground mt-3">Managed hosting, AI content, SEO, social & lead automation — billed monthly, cancel anytime.</p>
        </div>
        {plans.length > 0 && (
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4 mt-10 items-start">
            {plans.map((p) => {
              const Icon = PLAN_ICONS[p.key] || Zap;
              return (
                <div key={p.key} data-testid={`home-plan-${p.key}`}
                  className={`rounded-3xl glass p-6 relative ${p.popular ? "border-2 border-primary shadow-xl shadow-primary/20 lg:-translate-y-2" : ""}`}>
                  {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-bold">Most popular</span>}
                  <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><Icon className="h-5 w-5" /></div>
                  <h3 className="font-head font-bold text-xl mt-4">{p.name}</h3>
                  <p className="mt-3"><span className="font-head font-black text-4xl">${p.amount}</span><span className="text-muted-foreground">/mo</span></p>
                  <ul className="mt-4 space-y-2">
                    {p.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}</li>
                    ))}
                  </ul>
                  <Link to={p.custom ? "/accelerator" : `/get-started?plan=${p.key}`} data-testid={`home-plan-cta-${p.key}`}
                    className={`w-full mt-6 rounded-xl h-11 font-semibold inline-flex items-center justify-center gap-2 transition-[filter] hover:brightness-110 ${p.popular ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30" : "glass"}`}>
                    Choose {p.name} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-center text-sm text-muted-foreground mt-8">
          Need multi-location or B2B? <Link to="/accelerator" className="text-primary font-semibold hover:underline">See Enterprise & full plan details</Link>. Prefer a one-time build? <Link to="/pricing" className="text-primary font-semibold hover:underline">$299 website pricing</Link>.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-8" data-testid="seo-teaser">
        <div className="rounded-3xl glass p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center grid-bg">
          <div>
            <h2 className="font-head text-3xl font-extrabold">Free SEO Micro Tools</h2>
            <p className="text-muted-foreground mt-3">Enter your domain to check your domain authority, page speed, Google rank, ranking keywords — and get instant tips to climb higher.</p>
            <Link to="/seo-tools" data-testid="home-seo-cta" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 h-12 font-semibold hover:brightness-110 transition-[filter]">
              <Search className="h-5 w-5" /> Analyze My Website
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {["Domain Authority", "Page Speed", "Google Rank", "Ranking Keywords"].map((x) => (
              <div key={x} className="rounded-2xl bg-secondary/60 p-5">
                <div className="text-3xl font-head font-extrabold text-primary">?</div>
                <div className="text-xs text-muted-foreground mt-1">{x}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {reviews.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-16" data-testid="home-reviews">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-head text-3xl md:text-4xl font-extrabold">Loved by San Antonio businesses</h2>
              <p className="text-muted-foreground mt-2">Real feedback from clients we've designed and built for.</p>
            </div>
            <Link to="/reviews" className="hidden sm:inline-flex items-center gap-1 text-primary font-semibold">All reviews <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-5 md:grid-cols-3" data-testid="home-reviews-grid">
            {reviews.map((r) => (
              <div key={r.id} className="rounded-2xl glass-lite p-6">
                <StarRating value={r.rating} />
                <p className="mt-3 text-sm">{r.text}</p>
                <p className="mt-4 font-head font-bold text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.company}{r.location ? ` · ${r.location}` : ""}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 py-12" data-testid="home-areas">
        <div className="rounded-3xl glass p-8 md:p-12 grid-bg">
          <h2 className="font-head text-3xl md:text-4xl font-extrabold">San Antonio web design — and everywhere nearby</h2>
          <p className="text-muted-foreground mt-3 max-w-3xl">
            We provide web design, local SEO, PPC advertising, social media marketing, ecommerce development, IT consulting,
            computer &amp; mobile repair and secure hosting for small businesses across San Antonio and the surrounding
            Bexar County area. Whether you need a new WordPress site, a custom 3D website, a Google Business Profile that
            ranks, or a website redesign that finally converts, our San Antonio team delivers measurable results.
          </p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="home-areas-links">
            {AREAS.map((a) => (
              <Link key={a.slug} to={`/areas/${a.slug}`}
                className="rounded-full glass-lite px-4 py-1.5 text-sm font-medium hover:text-primary hover:-translate-y-0.5 transition-all">
                {a.name}
              </Link>
            ))}
          </div>
          <Link to="/areas" className="mt-6 inline-flex items-center gap-2 text-primary font-semibold">
            See all areas we serve <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <DealsSignup />
      <CTASection />
    </>
  );
}
